import { useMemo, useState } from 'react';
import { useUrlParam } from '../lib/url';
import { getConsensusClient, sign, DEVNET_VALIDATOR1 } from '../lib/client';
import { extractErrorMessage } from '../lib/errors';
import {
  INDEXING_SUBGROVE_EXAMPLES,
  buildBlockchainIndexingMode,
  IndexingSubgroveExample,
} from '../examples/indexing-subgroves';
import { InfoToggle } from '../components/InfoToggle';
import { Select } from '../components/Select';
import { ExamplePickerModal } from '../components/ExamplePickerModal';

// Blank starting point for a BlockchainIndexing config — the form no longer
// auto-loads a specific example. Real default config numbers (matching the
// SDK) so a hand-written config is valid once the addresses/events are filled.
const BI_SCAFFOLD = JSON.stringify(
  {
    label: '',
    description: '',
    subgrove_id: '',
    schema:
      'type Event @entity {\n  id: ID!\n  blockNumber: BigInt!\n  timestamp: BigInt!\n  transactionHash: String!\n}',
    execution_mode: 'ConsensusExecution',
    indexer_config: {
      min_indexers: 1,
      max_indexers: 3,
      reward_per_epoch: '100000000000000000',
      min_indexer_stake: '100000000000000000000000',
    },
    manifest: {
      spec_version: '1.0.0',
      description: '',
      data_sources: [
        {
          name: '',
          network: 'mainnet',
          address: '',
          abi: '',
          start_block: 0,
          events: ['Event(address indexed,uint256)'],
        },
      ],
    },
  },
  null,
  2,
);

interface Props {
  apiUrl: string;
}

type Mode = 'DataStorage' | 'FileStorage' | 'BlockchainIndexing';

type TxState =
  | { kind: 'idle' }
  | { kind: 'loading'; step: string }
  | { kind: 'ok'; message: string; txHash?: string }
  | { kind: 'error'; message: string };

// Convert a human amount like "1" (WILL) into the smallest-unit decimal
// string (1e18 per WILL). Validators reject non-integer amounts, so this
// must be done with integer math — `Math.floor` on a float is fine here
// because the Register form limits funding to two decimal places.
function willToSmallestUnit(will: string): string {
  const v = parseFloat(will);
  if (!Number.isFinite(v) || v < 0) return '0';
  return BigInt(Math.floor(v * 1e18)).toString();
}

const MODES: Mode[] = ['BlockchainIndexing', 'DataStorage', 'FileStorage'];

export function RegisterSubgrovePanel({ apiUrl }: Props) {
  const [modeRaw, setMode] = useUrlParam('mode', 'BlockchainIndexing');
  const mode: Mode = (MODES as string[]).includes(modeRaw) ? (modeRaw as Mode) : 'BlockchainIndexing';

  // ---- Fields shared by all modes ----
  const [subgroveId, setSubgroveId] = useState('');
  const [funding, setFunding] = useState('1');
  const [state, setState] = useState<TxState>({ kind: 'idle' });

  // ---- DataStorage-specific ----
  const [dsName, setDsName] = useState('');

  // ---- FileStorage-specific ----
  const [fsName, setFsName] = useState('');
  const [fsMaxFileSizeMb, setFsMaxFileSizeMb] = useState('100');
  const [fsReplication, setFsReplication] = useState('1');

  // ---- BlockchainIndexing-specific ----
  // Starts blank (scaffold). Users load an example via the picker modal or
  // hand-write the config; either way it's fully editable before submit.
  const [biConfigJson, setBiConfigJson] = useState<string>(BI_SCAFFOLD);
  const [exampleModalOpen, setExampleModalOpen] = useState(false);

  // Parsed config for the live hint — null when blank/scaffold/invalid so the
  // hint never claims a subgrove is loaded before the user picks one.
  const loadedConfig: IndexingSubgroveExample | null = useMemo(() => {
    if (biConfigJson.trim() === BI_SCAFFOLD.trim()) return null;
    try {
      const parsed = JSON.parse(biConfigJson) as IndexingSubgroveExample;
      return parsed.manifest?.data_sources?.length ? parsed : null;
    } catch {
      return null;
    }
  }, [biConfigJson]);

  // Picking an example from the modal fills the config + id, then closes it.
  const loadExample = (ex: IndexingSubgroveExample) => {
    setBiConfigJson(JSON.stringify(ex, null, 2));
    setSubgroveId(ex.subgrove_id);
    setExampleModalOpen(false);
  };

  const submit = async () => {
    if (!subgroveId) {
      setState({ kind: 'error', message: 'subgrove id required' });
      return;
    }

    setState({ kind: 'loading', step: 'Broadcasting RegisterSubgrove tx…' });

    try {
      const consensus = getConsensusClient(apiUrl);
      const fundingSmallest = willToSmallestUnit(funding);

      let modeObj: Parameters<typeof consensus.registerSubgrove>[1] extends never
        ? never
        : any;
      let schema = JSON.stringify({ version: 1, fields: {} });

      if (mode === 'DataStorage') {
        modeObj = {
          DataStorage: {
            name: dsName || subgroveId,
            writers: [DEVNET_VALIDATOR1.did],
            free_readers: [],
          },
        };
      } else if (mode === 'FileStorage') {
        const maxFileSize = Math.floor(parseFloat(fsMaxFileSizeMb) * 1024 * 1024);
        const replication = Math.max(1, parseInt(fsReplication, 10) || 1);
        modeObj = {
          FileStorage: {
            name: fsName || subgroveId,
            max_file_size: maxFileSize,
            replication_factor: replication,
            writers: [DEVNET_VALIDATOR1.did],
            free_readers: [],
          },
        };
      } else {
        // BlockchainIndexing
        let ex: IndexingSubgroveExample;
        try {
          ex = JSON.parse(biConfigJson) as IndexingSubgroveExample;
        } catch (err) {
          setState({
            kind: 'error',
            message: `Invalid JSON in config: ${err instanceof Error ? err.message : String(err)}`,
          });
          return;
        }
        schema = ex.schema;
        modeObj = buildBlockchainIndexingMode(ex);
      }

      const result = await consensus.registerSubgrove(
        subgroveId,
        schema,
        DEVNET_VALIDATOR1.did,
        DEVNET_VALIDATOR1.privateKey,
        DEVNET_VALIDATOR1.publicKeyId,
        sign,
        modeObj,
        undefined,
        fundingSmallest,
      );

      setState({
        kind: 'ok',
        message: `Subgrove "${subgroveId}" registered (${mode}).`,
        txHash: result.txHash,
      });
    } catch (err) {
      setState({ kind: 'error', message: extractErrorMessage(err) });
    }
  };

  return (
    <section className="panel">
      <h2>
        Register a subgrove
        <InfoToggle>
          Create a new subgrove. The mode determines what kind of data the subgrove holds
          and how it gets there. Transactions are signed with the devnet validator1 key
          and broadcast directly to CometBFT.
        </InfoToggle>
      </h2>

      <label className="form-field">
        <span>Mode</span>
        <Select
          value={mode}
          onChange={(v) => setMode(v as Mode)}
          options={MODES.map((m) => ({ value: m, label: m }))}
          data-testid="register-mode"
        />
      </label>

      <div className="form-row">
        <label className="form-field">
          <span>Subgrove ID</span>
          <input
            value={subgroveId}
            onChange={(e) => setSubgroveId(e.target.value)}
            placeholder="my-subgrove"
            spellCheck={false}
            data-testid="register-subgrove-id"
          />
        </label>
        <label className="form-field" style={{ flex: '0 0 140px' }}>
          <span>Initial funding (WILL)</span>
          <input
            type="number"
            value={funding}
            min={0}
            step={0.1}
            onChange={(e) => setFunding(e.target.value)}
            data-testid="register-funding"
          />
        </label>
      </div>

      {mode === 'DataStorage' && (
        <label className="form-field">
          <span>display name (optional)</span>
          <input
            value={dsName}
            onChange={(e) => setDsName(e.target.value)}
            placeholder={subgroveId || 'My Subgrove'}
            spellCheck={false}
          />
        </label>
      )}

      {mode === 'FileStorage' && (
        <>
          <label className="form-field">
            <span>display name (optional)</span>
            <input
              value={fsName}
              onChange={(e) => setFsName(e.target.value)}
              placeholder={subgroveId || 'My Files'}
              spellCheck={false}
            />
          </label>
          <div className="form-row">
            <label className="form-field">
              <span>max file size (MB)</span>
              <input
                type="number"
                value={fsMaxFileSizeMb}
                min={1}
                onChange={(e) => setFsMaxFileSizeMb(e.target.value)}
              />
            </label>
            <label className="form-field">
              <span>replication factor</span>
              <input
                type="number"
                value={fsReplication}
                min={1}
                max={10}
                onChange={(e) => setFsReplication(e.target.value)}
              />
            </label>
          </div>
        </>
      )}

      {mode === 'BlockchainIndexing' && (
        <>
          <div className="form-field">
            <div className="config-field-head">
              <span>Config</span>
              <button
                type="button"
                className="btn btn-small"
                onClick={() => setExampleModalOpen(true)}
                data-testid="register-load-example"
              >
                Load an example
              </button>
            </div>
            <textarea
              value={biConfigJson}
              onChange={(e) => setBiConfigJson(e.target.value)}
              rows={14}
              spellCheck={false}
              aria-label="Config"
              style={{ fontFamily: 'monospace', fontSize: '0.85rem' }}
              data-testid="register-bi-config"
            />
          </div>
          {loadedConfig && (
            <p className="field-help">
              Indexes {loadedConfig.manifest.data_sources.length}{' '}
              contract{loadedConfig.manifest.data_sources.length === 1 ? '' : 's'} on{' '}
              {loadedConfig.manifest.data_sources[0]?.network ?? 'mainnet'}. Edit the JSON
              above to change addresses, schema, or indexer config before registering.
            </p>
          )}
        </>
      )}

      <div className="form-row">
        <button
          className="btn btn-primary"
          onClick={submit}
          disabled={state.kind === 'loading'}
          data-testid="register-submit"
        >
          {state.kind === 'loading' ? state.step : `Register ${mode} subgrove`}
        </button>
      </div>

      {state.kind === 'ok' && (
        <div className="result" data-testid="register-result-ok">
          <div className="result-header">
            <span className="badge badge-ok">✓ registered</span>
          </div>
          <div className="result-body">
            <dl className="kv-list">
              <dt>message</dt>
              <dd>{state.message}</dd>
              {state.txHash && (
                <>
                  <dt>tx hash</dt>
                  <dd>
                    <code>{state.txHash}</code>
                  </dd>
                </>
              )}
            </dl>
          </div>
        </div>
      )}

      {state.kind === 'error' && (
        <div className="result" data-testid="register-result-err">
          <div className="result-header">
            <span className="badge badge-err">✗ error</span>
          </div>
          <pre className="result-body result-body-err">{state.message}</pre>
        </div>
      )}

      {exampleModalOpen && (
        <ExamplePickerModal
          examples={INDEXING_SUBGROVE_EXAMPLES}
          onSelect={loadExample}
          onClose={() => setExampleModalOpen(false)}
        />
      )}
    </section>
  );
}
